import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, RefreshCw, CheckCircle2, XCircle, Circle, Trash2, Download, FolderTree, ArrowUp } from "lucide-react";

import { SnapshotsApi, ServersApi, AccountsApi, RestoreApi } from "@/lib/services";
import type { SnapshotKind } from "@/lib/services";
import { fa } from "@/locales/fa";
import { formatBytes, jalaliDateTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 100;

// "all" is a sentinel because the shadcn Select component refuses to render
// a SelectItem with value="". We translate it to undefined when building
// the API query.
const ALL = "__all__";

const KIND_OPTIONS: SnapshotKind[] = ["files", "db", "db_create", "db_meta", "meta", "cpmove"];

export function SnapshotsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [serverId, setServerId] = useState<string>(ALL);
  const [accountQ, setAccountQ] = useState("");
  const [accountId, setAccountId] = useState<string>(ALL);
  const [kind, setKind] = useState<string>(ALL);
  const [validFilter, setValidFilter] = useState<string>(ALL); // ALL | "true" | "false"
  const [includePruned, setIncludePruned] = useState(false);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [page, setPage] = useState(0);
  const [browseId, setBrowseId] = useState<string | null>(null);

  // Use the same cache key + shape as ServersPage so we share the cache
  // entry (cheaper and prevents shape collisions). Both pages read the
  // Paginated { items, total } object and project .items where needed.
  const { data: serversData } = useQuery({
    queryKey: ["servers"],
    queryFn: () => ServersApi.list(),
  });
  const servers = serversData?.items ?? [];

  // Account picker: query for top-N suggestions when the operator types.
  // We don't pre-load all 46k accounts. When empty, the picker collapses to
  // "all" and the snapshots query just doesn't filter by account.
  const { data: accountSuggestions } = useQuery({
    queryKey: ["accounts-search", accountQ, serverId],
    queryFn: () =>
      AccountsApi.list({
        q: accountQ || undefined,
        serverId: serverId !== ALL ? serverId : undefined,
        limit: 25,
      }),
    enabled: accountQ.length > 0,
  });

  const filters = useMemo(
    () => ({
      serverId: serverId !== ALL ? serverId : undefined,
      accountId: accountId !== ALL ? accountId : undefined,
      kind: kind !== ALL ? (kind as SnapshotKind) : undefined,
      valid: validFilter === ALL ? undefined : validFilter === "true",
      prunedIncluded: includePruned,
      fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
      toDate: toDate
        // Inclusive end-of-day so picking a single day actually shows that day's rows.
        ? new Date(new Date(toDate).getTime() + 24 * 3600 * 1000 - 1).toISOString()
        : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [serverId, accountId, kind, validFilter, includePruned, fromDate, toDate, page],
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["snapshots", filters],
    queryFn: () => SnapshotsApi.list(filters),
    // Smooth pagination: keep previous page visible while next loads, so the
    // table doesn't blank-flash on every "Next" click.
    placeholderData: (prev) => prev,
  });

  const verify = useMutation({
    mutationFn: (id: string) => SnapshotsApi.verify(id),
    onSuccess: (r, id) => {
      const verified = r.verified ?? 0;
      const errors = r.errors ?? [];
      if (errors.length > 0) {
        toast.warning(fa.snapshots.verifyFail(errors[0]!));
      } else {
        toast.success(fa.snapshots.verifyOk(verified));
      }
      qc.invalidateQueries({ queryKey: ["snapshots"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      void id;
    },
    onError: (e: Error) => toast.error(fa.snapshots.verifyFail(e.message)),
  });

  // Quick-restore: from a single snapshot row, ask the backend for the
  // freshest valid snapshot set of that whole account (meta + files + db +
  // db_create + db_meta), then create a download_link RestoreRequest. The
  // user lands on /restore where they can grab the signed URL once ready.
  // We deliberately don't restore JUST the clicked snapshot — restorepkg
  // needs a complete cpmove tree, so a "restore one .sql.zst by itself"
  // would yield a broken account on the target server.
  const quickRestore = useMutation({
    mutationFn: async (accountId: string) => {
      const set = await RestoreApi.recommend(accountId);
      if (!set.files && !set.meta && !set.db_meta && (!set.db || Object.keys(set.db).length === 0)) {
        throw new Error("هیچ اسنپ‌شات معتبری برای این اکانت پیدا نشد");
      }
      return RestoreApi.create({
        accountId,
        snapshotIds: set,
        targetType: "download_link",
        expiresInHours: 72,
      });
    },
    onSuccess: () => {
      toast.success("درخواست بازیابی ساخته شد", {
        description: "در صفحه‌ی «بازیابی» منتظر آماده شدن لینک دانلود بمانید.",
        action: { label: "برو به بازیابی", onClick: () => navigate("/restore") },
      });
      qc.invalidateQueries({ queryKey: ["restores"] });
    },
    onError: (e: Error) =>
      toast.error("ساخت درخواست بازیابی ناموفق", { description: e.message }),
  });

  const resetFilters = () => {
    setServerId(ALL);
    setAccountQ("");
    setAccountId(ALL);
    setKind(ALL);
    setValidFilter(ALL);
    setIncludePruned(false);
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const total = data?.total ?? 0;
  const rows = data?.items ?? [];
  const from = rows.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = page * PAGE_SIZE + rows.length;
  const hasNext = to < total;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Archive className="size-5 text-primary" />
            {fa.snapshots.title}
          </h1>
          <p className="text-sm text-muted-foreground">{fa.snapshots.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && !isLoading && (
            <RefreshCw className="size-4 text-muted-foreground animate-spin" />
          )}
          <Badge variant="secondary" className="lat-num">
            {total.toLocaleString("en-US")}
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">فیلترها</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">{fa.snapshots.filters.server}</Label>
              <Select
                value={serverId}
                onValueChange={(v) => {
                  setServerId(v);
                  setPage(0);
                  // changing server invalidates the account picker
                  setAccountId(ALL);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{fa.snapshots.filters.allServers}</SelectItem>
                  {servers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label className="text-xs">{fa.snapshots.filters.account}</Label>
              <Input
                placeholder="جستجوی cPanel user…"
                value={accountQ}
                onChange={(e) => {
                  setAccountQ(e.target.value);
                  if (!e.target.value) setAccountId(ALL);
                  setPage(0);
                }}
              />
              {accountQ && (accountSuggestions?.items?.length ?? 0) > 0 && (
                <div className="max-h-32 overflow-auto rounded-md border border-border bg-popover text-popover-foreground text-xs">
                  {accountSuggestions!.items.slice(0, 8).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        setAccountId(a.id);
                        setAccountQ(a.user);
                        setPage(0);
                      }}
                      className={
                        "block w-full text-start px-3 py-1.5 hover:bg-muted lat-num " +
                        (accountId === a.id ? "bg-muted" : "")
                      }
                    >
                      <span className="font-mono">{a.user}</span>
                      {a.primaryDomain && (
                        <span className="text-muted-foreground"> — {a.primaryDomain}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label className="text-xs">{fa.snapshots.filters.kind}</Label>
              <Select
                value={kind}
                onValueChange={(v) => {
                  setKind(v);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{fa.snapshots.filters.allKinds}</SelectItem>
                  {KIND_OPTIONS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {fa.snapshots.kindLabels[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label className="text-xs">{fa.snapshots.filters.valid}</Label>
              <Select
                value={validFilter}
                onValueChange={(v) => {
                  setValidFilter(v);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{fa.snapshots.filters.allValid}</SelectItem>
                  <SelectItem value="true">{fa.snapshots.filters.validOnly}</SelectItem>
                  <SelectItem value="false">{fa.snapshots.filters.invalidOnly}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">از تاریخ</Label>
              <Input
                type="date"
                dir="ltr"
                className="lat-num"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">تا تاریخ</Label>
              <Input
                type="date"
                dir="ltr"
                className="lat-num"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(0); }}
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                className="size-3.5 cursor-pointer accent-primary"
                checked={includePruned}
                onChange={(e) => {
                  setIncludePruned(e.target.checked);
                  setPage(0);
                }}
              />
              {fa.snapshots.filters.includePruned}
            </label>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              {fa.snapshots.filters.reset}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              {fa.snapshots.empty}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fa.snapshots.columns.created}</TableHead>
                  <TableHead>{fa.snapshots.columns.server}</TableHead>
                  <TableHead>{fa.snapshots.columns.account}</TableHead>
                  <TableHead>{fa.snapshots.columns.kind}</TableHead>
                  <TableHead>{fa.snapshots.columns.dbName}</TableHead>
                  <TableHead className="text-end">{fa.snapshots.columns.size}</TableHead>
                  <TableHead>{fa.snapshots.columns.valid}</TableHead>
                  <TableHead>{fa.snapshots.columns.restic}</TableHead>
                  <TableHead className="text-end">{fa.snapshots.columns.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs">
                      <div className="lat-num" dir="ltr">{jalaliDateTime(s.createdAt)}</div>
                      <div className="text-[10px] text-muted-foreground lat-num" dir="ltr">
                        {new Date(s.createdAt).toISOString().slice(0, 19).replace("T", " ")} UTC
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{s.serverName}</TableCell>
                    <TableCell className="text-xs font-mono lat-num" dir="ltr">
                      {s.accountUser ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {fa.snapshots.kindLabels[s.kind as SnapshotKind] ?? s.kind}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono lat-num" dir="ltr">
                      {s.dbName ?? "—"}
                    </TableCell>
                    <TableCell className="text-end text-xs lat-num" dir="ltr">
                      {s.sizeBytes != null ? formatBytes(s.sizeBytes) : "—"}
                    </TableCell>
                    <TableCell>
                      <ValidityCell snap={s} />
                    </TableCell>
                    <TableCell className="text-[10px] font-mono lat-num text-muted-foreground" dir="ltr">
                      {s.restic.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        {/* Quick-restore: only enabled when the snapshot is
                            attached to an account (orphaned snapshots from
                            deleted users can't be quick-restored), valid,
                            and not pruned. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={
                            !s.accountId ||
                            !s.valid ||
                            s.pruned ||
                            (quickRestore.isPending && quickRestore.variables === s.accountId)
                          }
                          onClick={() => s.accountId && quickRestore.mutate(s.accountId)}
                          title={
                            !s.accountId
                              ? "اکانت یتیم — quick-restore ممکن نیست"
                              : s.pruned
                              ? "این اسنپ‌شات حذف فیزیکی شده"
                              : !s.valid
                              ? "اسنپ‌شات سالم نیست — اول بازبینی کنید"
                              : "ساخت لینک دانلود از آخرین بکاپ معتبر این اکانت"
                          }
                        >
                          <Download
                            className={
                              "size-3.5 " +
                              (quickRestore.isPending && quickRestore.variables === s.accountId
                                ? "animate-pulse text-primary"
                                : "")
                            }
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={s.pruned || (s.kind !== "files" && s.kind !== "cpmove")}
                          onClick={() => setBrowseId(s.id)}
                          title={
                            s.pruned
                              ? "اسنپ‌شات حذف فیزیکی شده"
                              : s.kind !== "files" && s.kind !== "cpmove"
                                ? "فقط برای اسنپ‌شات‌های files/cpmove قابل مرور"
                                : "مرور فایل‌ها"
                          }
                        >
                          <FolderTree className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={verify.isPending && verify.variables === s.id}
                          onClick={() => verify.mutate(s.id)}
                          title={fa.snapshots.verify}
                        >
                          <RefreshCw
                            className={
                              "size-3.5 " +
                              (verify.isPending && verify.variables === s.id ? "animate-spin" : "")
                            }
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SnapshotBrowseDialog
        snapshotId={browseId}
        onClose={() => setBrowseId(null)}
      />

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="lat-num" dir="ltr">
            {fa.snapshots.showing(from, to, total)}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              {fa.snapshots.prev}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              {fa.snapshots.next}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ValidityCell({
  snap,
}: {
  snap: {
    valid: boolean;
    pruned: boolean;
    verifiedAt: string | null;
    verifyError: string | null;
  };
}) {
  if (snap.pruned) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="forgotten by retention/orphan pass">
        <Trash2 className="size-3" />
        {fa.snapshots.pruned}
      </span>
    );
  }
  if (snap.valid) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-400"
        title={snap.verifiedAt ?? ""}
      >
        <CheckCircle2 className="size-3" />
        {fa.snapshots.valid}
      </span>
    );
  }
  if (snap.verifyError) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-destructive"
        title={snap.verifyError}
      >
        <XCircle className="size-3" />
        {fa.snapshots.invalid}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Circle className="size-3" />
      {fa.snapshots.notVerified}
    </span>
  );
}

/* ─────────────────────────── Browse modal ─────────────────────────── */

function SnapshotBrowseDialog({
  snapshotId,
  onClose,
}: {
  snapshotId: string | null;
  onClose: () => void;
}) {
  const [path, setPath] = useState<string>("");
  // Reset path each time a different snapshot is opened.
  useEffect(() => {
    if (snapshotId) setPath("");
  }, [snapshotId]);
  const open = snapshotId != null;

  const q = useQuery({
    queryKey: ["snapshot-ls", snapshotId, path],
    queryFn: () =>
      SnapshotsApi.ls(snapshotId!, { path: path || undefined, limit: 5000 }),
    enabled: open,
  });

  const entries = q.data?.entries ?? [];
  const parent = path ? path.split("/").slice(0, -1).join("/") : "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPath(""); onClose(); } }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="size-4 text-primary" />
            مرور محتویات اسنپ‌شات
          </DialogTitle>
          <DialogDescription className="lat-num text-[11px]" dir="ltr">
            {path || "/"} · {q.data ? `${q.data.count} مورد${q.data.truncated ? " (truncated)" : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!path}
            onClick={() => setPath(parent)}
          >
            <ArrowUp className="size-4" /> بالاتر
          </Button>
          <Input
            dir="ltr"
            className="lat-num"
            placeholder="/home/user/public_html"
            value={path}
            onChange={(e) => setPath(e.target.value)}
          />
        </div>
        {q.isLoading ? (
          <Skeleton className="h-64" />
        ) : q.isError ? (
          <div className="text-destructive text-xs">
            {(q.error as Error)?.message ?? "خطا"}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            خالی است.
          </div>
        ) : (
          <ul className="max-h-[55vh] overflow-auto rounded-md border border-border divide-y divide-border">
            {entries.map((e, i) => {
              const isDir = e.type === "dir";
              const full = e.path ?? (path ? `${path}/${e.name ?? ""}` : (e.name ?? ""));
              return (
                <li key={`${full}-${i}`}>
                  <button
                    type="button"
                    disabled={!isDir}
                    onClick={() => isDir && setPath(full)}
                    className={
                      "flex w-full items-center gap-2 px-3 py-1.5 text-xs lat-num text-start " +
                      (isDir ? "hover:bg-muted/40 cursor-pointer" : "cursor-default")
                    }
                    dir="ltr"
                  >
                    <span className="font-mono">{isDir ? "📁" : "📄"}</span>
                    <span className="flex-1 truncate">{e.name ?? full}</span>
                    {e.size != null && !isDir && (
                      <span className="text-muted-foreground tabular">{formatBytes(e.size)}</span>
                    )}
                    {e.mtime && (
                      <span className="text-muted-foreground/70">{e.mtime.slice(0, 10)}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

